'use client';

import { useEffect, useId, useState } from 'react';
import type { ResolvedOffer } from './types';
import { Button } from '@/components/Button';
import { Icon } from '@/components/Icon';
import { Modal } from '@/components/Modal';
import type { EventTrackingResolved } from '@/lib/gtm/pushCmsEventTracking';
import { pushCmsEventTracking } from '@/lib/gtm/pushCmsEventTracking';
import { RichText } from '@/lib/render/RichText';
import { substituteHref } from '@/lib/render/substituteHref';

interface SmallOfferCardProps {
  offer: ResolvedOffer;
  sub: (s: string) => string;
  phoneTracking?: EventTrackingResolved[];
  /** The widget's own section title, shown as the modal's headline. */
  widgetTitle?: string;
}

export function SmallOfferCard({
  offer,
  sub,
  phoneTracking,
  widgetTitle,
}: SmallOfferCardProps) {
  /*
   * A small offer is a franchise partner's own content, and it is not trusted
   * enough to appear in the page an SH domain serves. So none of it is: the
   * card draws nothing until React has hydrated, which leaves the whole widget
   * out of the server response — the category label, the title, the CTA text
   * and the dialog whose `aria-label` repeats the title (SH-564).
   *
   * The offer still travels with the page, as hydration data in the RSC
   * payload, so there is no second request and no spinner. What changes is who
   * assembles it: the browser rather than the server, which is what keeps it
   * out of the markup a crawler or a scraper reads.
   *
   * The effect runs after the first client render, and that first render
   * returns the same `null` the server did, so hydration matches.
   */
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    setHydrated(true);
  }, []);

  const [open, setOpen] = useState(false);
  /*
   * The fragment the CTA asked for, held until the dialog is actually gone.
   * Set on the click; performed by the effect below, never in the handler —
   * the jump has to happen after `Modal` has run `dialog.close()`, or it
   * scrolls a document that `html:has(dialog[open]) { overflow: hidden }`
   * (globals.css) still has pinned and the open dialog still covers.
   */
  const [pendingAnchor, setPendingAnchor] = useState<string | null>(null);
  const categoryLabel = offer.categoryLabel ? sub(offer.categoryLabel) : '';
  const title = offer.title ? sub(offer.title) : '';
  const cardCtaText = offer.ctaButtonText ? sub(offer.ctaButtonText) : '';
  const modalCtaLabel = offer.ctaLink?.label ? sub(offer.ctaLink.label) : '';
  // `useId()` rather than the category label: two offers in one widget may
  // share a label, and duplicate DOM ids would point every `aria-labelledby`
  // at the first modal's heading.
  const titleId = useId();
  /*
   * The widget's section title heads the modal, so the dialog names the set the
   * offer came from. Falls back to the offer's own category label when the
   * widget has no title — the title carries the dialog's accessible name, and
   * dropping it would leave `aria-labelledby` pointing at nothing.
   */
  const modalHeadline = widgetTitle ? sub(widgetTitle) : categoryLabel;

  const ctaHref =
    substituteHref(offer.ctaLink?.href, sub) || '#beratungstermin';
  /*
   * Only a bare fragment stays on this page and so needs handling. Any other
   * target — including `/preise#tarife` — replaces the document, which
   * disposes of the dialog by itself, so that click is left alone.
   */
  const ctaAnchor = ctaHref.startsWith('#') ? ctaHref.slice(1) : null;

  /*
   * `Modal` is a child, so its cleanup — the `dialog.close()` — has already
   * run by the time this effect does. Moving the browser to the fragment
   * rather than calling `scrollIntoView` on it: the target may be a
   * Collapsible Block, a Subjects tab or a Hero card, each of which opens
   * itself on `hashchange` and would stay shut behind a silent scroll.
   */
  useEffect(() => {
    if (open || !pendingAnchor) {
      return;
    }
    setPendingAnchor(null);
    const target = document.getElementById(pendingAnchor);
    if (!target) {
      return;
    }
    // Re-opening an offer while already parked on its anchor changes no hash,
    // so there is no fragment jump to inherit — the same same-hash gap SH-204
    // found in the Collapsible Block.
    if (window.location.hash === `#${pendingAnchor}`) {
      target.scrollIntoView();
    } else {
      window.location.hash = pendingAnchor;
    }
  }, [open, pendingAnchor]);

  // After every hook, so the hook order is the same on both renders.
  if (!hydrated) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => {
          pushCmsEventTracking(phoneTracking, { label: cardCtaText });
          setOpen(true);
        }}
        aria-haspopup="dialog"
        className="bg-secondary-lighter hover:bg-secondary-lighter-hover focus-visible:ring-primary flex w-full cursor-pointer flex-col items-center justify-between gap-2 rounded-2xl px-6 py-4 text-center transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none md:flex-row md:text-left"
      >
        <div>
          {categoryLabel && <p className="h5">{categoryLabel}</p>}
          {title && <span>{title}</span>}
        </div>
        {cardCtaText && (
          <span className="text-primary flex shrink-0 items-center font-medium">
            &#9733; {cardCtaText}
            <Icon
              src="/icons/svg/chevron-right.svg"
              size={20}
              className="shrink-0"
            />
          </span>
        )}
      </button>

      {/*
        The body is built only while the dialog is open, so a small offer's
        subtitle, benefits and CTA are in no server response until a visitor
        asks for them (SH-564). The card itself — category, title, CTA text —
        stays in the HTML; it is what the page advertises.

        `titleId` goes with the body: pointing `aria-labelledby` at a heading
        that is not rendered would leave the dialog unnamed, so while closed
        the dialog falls back to naming itself after the offer.
      */}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        titleId={open && modalHeadline ? titleId : undefined}
        ariaLabel={
          open && modalHeadline ? undefined : title || cardCtaText || undefined
        }
      >
        {open && (
          <>
            {/*
              A `<p>` styled as an `h2`, not a heading: a modal title is never
              a heading element (70.15-Heading-Structure § 5). Not rendered
              when empty; the dialog then takes its name from the offer title,
              or the card's CTA text. No `text-base`/`font-medium`: the base
              `.h2` in globals.css is already 16px at weight 500, and
              Tailwind's `text-base` would replace the design's 20px
              line-height with its own 1.5 ratio.
            */}
            {modalHeadline && (
              <p id={titleId} className="h2 mb-4 text-center">
                {modalHeadline}
              </p>
            )}
            {offer.subTitle && <p className="mb-4">{sub(offer.subTitle)}</p>}
            {offer.benefits && (
              <div className="mb-8">
                <RichText nodes={offer.benefits} />
              </div>
            )}
            {modalCtaLabel && (
              <div className="flex justify-center">
                <Button
                  href={ctaHref}
                  onClick={
                    ctaAnchor
                      ? (e) => {
                          e.preventDefault();
                          setOpen(false);
                          setPendingAnchor(ctaAnchor);
                        }
                      : undefined
                  }
                  variant="primary"
                  size="md"
                  className="w-full"
                >
                  {modalCtaLabel}
                </Button>
              </div>
            )}
          </>
        )}
      </Modal>
    </>
  );
}
